import { useState, useRef, useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { Button, Card, CardContent } from '@jeraisonne/ui';
import { useJoinClassByCode } from '@/lib/student/join-class';

interface Props {
  studentId: string;
}

export function JoinClassCard({ studentId }: Props) {
  const [code, setCode] = useState('');
  const [success, setSuccess] = useState<string | null>(null);
  const join = useJoinClassByCode(studentId);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    try {
      const result = await join.mutateAsync(code);
      setSuccess(result.class_name);
      setCode('');
    } catch {
      // error shown via join.error
    }
  };

  if (success) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/60">
        <CardContent className="space-y-2 py-4 text-center">
          <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-500" />
          <p className="text-ink-900 font-display font-bold">
            Tu as rejoint <span className="text-primary-700">«&nbsp;{success}&nbsp;»</span>&nbsp;!
          </p>
          <button
            type="button"
            onClick={() => setSuccess(null)}
            className="text-ink-500 hover:text-ink-700 text-xs"
          >
            Rejoindre une autre classe
          </button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-ink-200">
      <CardContent className="space-y-3 py-4">
        <div>
          <p className="text-ink-900 text-sm font-semibold">Rejoindre ma classe</p>
          <p className="text-ink-500 mt-0.5 text-xs">
            Saisis le code de 8 caractères donné par ton professeur.
          </p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            ref={inputRef}
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            placeholder="Ex. : AB3D7FGH"
            maxLength={8}
            className="border-ink-200 font-display text-ink-900 placeholder:text-ink-300 focus:ring-primary-400 h-11 w-full rounded-xl border bg-white px-4 text-center text-lg font-bold uppercase tracking-widest focus:outline-none focus:ring-2"
          />
          {join.error && (
            <p className="text-accent-600 text-center text-xs">{join.error.message}</p>
          )}
          <Button
            type="submit"
            size="sm"
            className="w-full"
            disabled={code.length < 4 || join.isPending}
          >
            {join.isPending ? 'Vérification…' : 'Rejoindre'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
